import React, {useCallback, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchDevices} from '@/entities/device/api';
import {
  deviceTypeIcon,
  deviceTypeLabelKey,
  isCameraLike,
  managingDesktop,
  type DeviceSummary,
} from '@/entities/device/types';
import {IconBubble, PressCard} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {Icon} from '@/shared/ui/icons';
import {SkeletonList} from '@/shared/ui/skeleton';
import {StatusPill, StatusDot, type StatusTone} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {useContentInsets} from '@/shared/ui/screen';
import {SplitView} from '@/shared/ui/split-view';
import {DataList} from '@/shared/ui/data-list';
import {Cell, NameCell, type Column} from '@/shared/ui/table';
import {FilterSelect} from '@/shared/ui/filter-bar';
import {sortParam, type SortAccessors} from '@/shared/lib/table-sort';
import {
  boolParam,
  enumParam,
  intParam,
  stringParam,
  useParamState,
} from '@/shared/lib/use-param-state';
import {formatTimestamp} from '@/shared/lib/format';

/**
 * The household's terminals. Note the offline tone: an offline device is MUTED GREY,
 * never red — a dead camera is not an alarm, and the red destructive tone is reserved
 * for a denied pass. Online is the live teal.
 *
 * Each row leads with a terminal glyph in a tinted bubble whose wash IS the state:
 * teal for a device that is answering, grey for one that is not. At list scale that
 * bubble is readable a metre away, which a 8pt dot next to a name is not.
 *
 * Desktop trades the cards for a table and a detail pane — see PeopleList for why. A
 * fleet is read by comparing columns (which door, where, how many people, answering or
 * not, when last), and columns are the one thing a stack of cards cannot give.
 *
 * ## Kinds
 *
 * Since 2026-08-11 a row says what it IS (`device_type`), and the list can fold in the
 * desktop bridges that used to need a screen of their own (`include_desktops`). Both
 * show up here rather than in a filter bar because the question they answer —
 * "what is standing on my site?" — is the question this screen exists for. The glyph
 * carries the kind at list scale; the label under the name carries it in words, since
 * a terminal and a barrier are both boxes and the mark alone would be a guess.
 *
 * The kind is never printed raw: `faceid_terminal` is a machine token, and the
 * changelog asks specifically that it not reach a user untranslated.
 */
const PER_PAGE = 50;
/** Строк в странице сетки, и что предлагает рейка под ней. */
const PAGE_SIZE = 20;

type DeviceStatus = 'all' | 'online' | 'offline';

/* Кодеки адреса — модульные, чтобы ссылка на них была стабильной. */
const STATUS_PARAM = enumParam<DeviceStatus>(['all', 'online', 'offline'], 'all');
const NAME_SORT = {key: 'name', direction: 'asc'} as const;
const SORT_PARAM = sortParam(NAME_SORT);
const PAGE_PARAM = intParam(1);
const PAGE_SIZE_PARAM = intParam(PAGE_SIZE);
const PAGE_SIZES = [20, 50] as const;

/** Порядки, предлагаемые чипами на узком окне, и подписи к ним. */
const PHONE_SORTS = ['name', 'status', 'type', 'seen'] as const;
const PHONE_SORT_LABELS: Record<string, string> = {
  name: 'home.devices.colName',
  status: 'home.devices.colStatus',
  type: 'home.devices.colType',
  seen: 'home.devices.colSeen',
};

/** Online → live teal; offline → muted grey (`neutral`), deliberately not red. */
function deviceTone(online: boolean): StatusTone {
  return online ? 'online' : 'neutral';
}

export type DeviceListProps = {
  companyId: string;
  /** Phone: open the terminal on its own screen. Desktop uses `renderDetail`. */
  onOpen: (deviceId: string, name: string) => void;
  /** Opens the QR adopt flow. Shown as a header CTA — visible even when the list is empty. */
  onAdopt?: () => void;
  /** Desktop: the card to show in the pane beside the table. */
  renderDetail?: (deviceId: string, name: string) => React.ReactNode;
  /**
   * Opens the batch-operations sheet. Two faces, as on the personnel list: a quiet
   * icon beside the phone header's CTA, a flat verb on the console strip.
   */
  /** @deprecated Пакетные операции — глагол шапки страницы (`DevicesScreen`). */
  onOpenTools?: () => void;
};

const styles = StyleSheet.create({
  header: {paddingTop: spacing(3), paddingBottom: spacing(3)},
  headerStack: {gap: spacing(3)},
  headerToggle: {flexShrink: 1, minWidth: 0},

  // Three figures across, the total pushed to the far edge so the pair that gets
  // compared — up and down — sits together on the left.
  deck: {flexDirection: 'row', alignItems: 'flex-start', gap: spacing(6)},
  deckStat: {gap: 2},
  deckTotal: {flex: 1, alignItems: 'flex-end'},
  list: {
    gap: spacing(2),
    paddingBottom: spacing(6),
    width: '100%',
    alignSelf: 'center',
    flexGrow: 1,
  },
  listEmpty: {justifyContent: 'center'},
  columns: {gap: spacing(3)},
  cell: {flex: 1},
  // Desktop: one toolbar line — the switch that changes what the list CONTAINS on the
  // left, next to the list it governs, and the button that adds to it on the right.
  // Stacked and right-aligned, which is what this was, put a two-line hint hard against
  // the right edge above a left-aligned table and read as debris.
  headerWide: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing(5),
  },
  deskList: {gap: spacing(3), paddingBottom: spacing(6)},
  /* Desktop: the list side is ONE docked panel — a toolbar strip, the grid, and a
     footer of counts — which is the shape every list in the console has. It fills the
     column rather than growing, so the strip and the counts stay put while the rows
     scroll under them. */
  deskFrame: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    alignSelf: 'center',
    paddingBottom: spacing(4),
  },
  deskScroll: {paddingBottom: spacing(2)},
  /* Счётчики парка — в подвале, рядом с диапазоном. */
  footStat: {flexDirection: 'row', alignItems: 'center', gap: 6},

  row: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  body: {flex: 1, gap: 3},
  meta: {alignItems: 'flex-end', gap: 3},
});

/** Memoised; takes the stable `onOpen` rather than a per-render arrow (see PeopleList). */
const DeviceRow = React.memo(function DeviceRow({
  device,
  onOpen,
}: {
  device: DeviceSummary;
  onOpen: (deviceId: string, name: string) => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const online = device.online;

  return (
    <PressCard
      padding={3}
      onPress={() => onOpen(device.device_id, device.device_name)}
      accessibilityLabel={device.device_name}>
      <View style={styles.row}>
        <IconBubble tone={online ? p.signalSoft : p.muted} size={42} square>
          <Icon
            name={deviceTypeIcon(device.device_type)}
            size={21}
            color={online ? p.signal : p.mutedForeground}
          />
        </IconBubble>

        <View style={styles.body}>
          <AppText variant="subtitle" numberOfLines={1}>
            {device.device_name}
          </AppText>
          {/* The machine number outlives any rename: it is what the terminal shows on
              its own screen and what a person reads out to support. */}
          {device.machine_number ? (
            <AppText variant="mono" color={p.mutedForeground} numberOfLines={1}>
              {device.machine_number}
            </AppText>
          ) : null}
          <AppText variant="caption" numberOfLines={1}>
            {[
              t(deviceTypeLabelKey(device.device_type)),
              managingDesktop(device) ? t('home.device.managedBy.short') : null,
              device.model || null,
              t('home.devices.personnelCount', {count: device.personnel_count}),
            ]
              .filter(Boolean)
              .join(' · ')}
          </AppText>
        </View>

        <View style={styles.meta}>
          <StatusPill
            tone={deviceTone(online)}
            size="sm"
            label={t(online ? 'home.devices.online' : 'home.devices.offline')}
          />
          <AppText variant="mono" color={p.mutedForeground}>
            {formatTimestamp(device.last_seen_at)}
          </AppText>
        </View>
      </View>
    </PressCard>
  );
});

export function DeviceList({
  companyId,
  onOpen,
  onAdopt,
  renderDetail,
}: DeviceListProps) {
  const {t} = useTranslation();
  const p = useTheme();
  const insets = useContentInsets();
  const {columns, isDesktop} = useBreakpoint();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Отбор и порядок — в адресе, как и в остальных списках кабинета.
  const [includeDesktops, setIncludeDesktops] = useParamState('desktops', false, boolParam);
  /* The console's toolbar carries a status filter and a search box, and a strip with
     neither would be the SHAPE of a toolbar with none of its work. Both filter the
     loaded page in the client: the query already fetches the whole estate (PER_PAGE
     is 50 and a household has tens of doors), so a round trip per keystroke would buy
     nothing. */
  const [status, setStatus] = useParamState('status', 'all' as DeviceStatus, STATUS_PARAM);
  /** Терминал, камера, мост — три разных предмета в одном списке. */
  const [kind, setKind] = useParamState('kind', '', stringParam);
  const [departmentId, setDepartmentId] = useParamState('department', '', stringParam);
  const [search, setSearch] = useParamState('q', '', stringParam);
  const [pageSize, setPageSize] = useParamState('per', PAGE_SIZE, PAGE_SIZE_PARAM);
  const [pageParam, setPageParam] = useParamState('page', 1, PAGE_PARAM);
  const [sortState, setSortState] = useParamState('sort', NAME_SORT, SORT_PARAM);

  const query = useQuery({
    // The flag is part of the key: with it in the params but not the key, turning it
    // on would show the cached terminals-only page until a refetch happened to land.
    queryKey: ['devices', companyId, includeDesktops],
    queryFn: () => fetchDevices(companyId, {perPage: PER_PAGE, includeDesktops}),
  });

  // Cameras and recorders live under their own tab, catalogued by the stream
  // snapshot. Here they were a source row AND a row per channel, typed «Камера»
  // among the terminals, with a terminal's panel on click (2026-10-01).
  const items = (query.data?.data ?? []).filter(d => !isCameraLike(d.device_type));

  const needle = search.trim().toLowerCase();
  const matched = items.filter(d => {
    if (status === 'online' && !d.online) {
      return false;
    }
    if (status === 'offline' && d.online) {
      return false;
    }
    if (kind && d.device_type !== kind) {
      return false;
    }
    if (departmentId && (d.department_name ?? '') !== departmentId) {
      return false;
    }
    if (!needle) {
      return true;
    }
    // Name, place, model — and the machine number, because «SVFID-…» is what the
    // terminal itself shows and what a rename cannot take away.
    return [d.device_name, d.location_label, d.model, d.machine_number]
      .filter(Boolean)
      .some(field => String(field).toLowerCase().includes(needle));
  });

  /*
    Сортировка ЧЕСТНАЯ: `fetchDevices` привёз весь парк одной страницей, отбор идёт по
    привезённому, и разбивка под таблицей тоже клиентская. Поэтому «сначала офлайн»
    означает офлайн во всём парке, а не на видимой странице, — в отличие от списков с
    серверной разбивкой, где такой заголовок соврал бы.
  */
  const ACCESSORS: SortAccessors<DeviceSummary> = {
    name: d => d.device_name,
    type: d => d.device_type,
    location: d => d.location_label,
    department: d => d.department_name ?? null,
    personnel: d => d.personnel_count,
    status: d => d.online,
    seen: d => d.last_seen_at ?? null,
  };
  // Порядок и страницы ведёт сам список (`shared/ui/data-list`); сюда доезжает
  // только отбор — по нему же считаются числа в подвале.
  const rows = matched;
  const onlineCount = rows.filter(d => d.online).length;

  /** Отделы, которые ВСТРЕЧАЮТСЯ в парке: справочник тут был бы длиннее правды. */
  const departmentOptions = [
    {value: '', label: t('actions.any')},
    ...Array.from(
      new Set(items.map(d => d.department_name).filter((n): n is string => !!n)),
    )
      .sort((a, b) => a.localeCompare(b))
      .map(name => ({value: name, label: name})),
  ];

  /** Типы, которые встречаются в парке, — по той же причине. */
  const kindOptions = [
    {value: '', label: t('actions.any')},
    ...Array.from(new Set(items.map(d => d.device_type).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b))
      .map(value => ({value, label: t(deviceTypeLabelKey(value))})),
  ];

  const filters = (
    <>
      <FilterSelect
        label={t('home.devices.colStatus')}
        value={status}
        options={[
          {value: 'all', label: t('actions.any')},
          {value: 'online', label: t('home.devices.online')},
          {value: 'offline', label: t('home.devices.offline')},
        ]}
        onChange={next => setStatus(next as 'all' | 'online' | 'offline')}
        neutral="all"
        phone={!isDesktop}
        testID="device-status"
      />
      <FilterSelect
        label={t('home.devices.colType')}
        value={kind}
        options={kindOptions}
        onChange={setKind}
        phone={!isDesktop}
        testID="device-kind"
      />
      {departmentOptions.length > 1 ? (
        <FilterSelect
          label={t('reports.departmentFilter')}
          value={departmentId}
          options={departmentOptions}
          onChange={setDepartmentId}
          phone={!isDesktop}
          testID="device-department"
        />
      ) : null}
      {/* Переключатель того, ЧТО в списке, — той же пилюлей, что и остальные отборы:
          он отвечает на тот же вопрос «почему тут нет/есть эта строка». Крашен, когда
          мосты ПОКАЗАНЫ: это положение, в котором список шире обычного. */}
      <FilterSelect
        label={t('home.devices.desktopsShort')}
        value={includeDesktops ? 'yes' : 'no'}
        options={[
          {value: 'no', label: t('home.devices.desktopsHidden')},
          {value: 'yes', label: t('home.devices.desktopsShown')},
        ]}
        onChange={next => setIncludeDesktops(next === 'yes')}
        neutral="no"
        phone={!isDesktop}
        testID="device-desktops"
      />
    </>
  );

  const dirty = status !== 'all' || !!kind || !!departmentId || includeDesktops;


  const empty = query.isError ? (
    <EmptyState
      tone="error"
      title={t('home.devices.error')}
      actionLabel={t('actions.retry')}
      onAction={() => {
        void query.refetch();
      }}
    />
  ) : (
    <EmptyState
      icon="devices"
      title={t('home.devices.empty')}
      actionLabel={onAdopt ? t('home.adopt.cta') : undefined}
      onAction={onAdopt}
    />
  );

  /** Та же строка там, где столбцов нет. Раскладку в колонки ведёт список. */
  const renderCard = useCallback(
    (item: DeviceSummary) => <DeviceRow device={item} onOpen={onOpen} />,
    [onOpen],
  );

  const cols: Column<DeviceSummary>[] = [
    {
      key: 'name',
      sortKey: 'name',
      title: t('home.devices.colName'),
      flex: 2,
      render: d => (
        <NameCell
          mark={
            <IconBubble tone={d.online ? p.signalSoft : p.muted} size={28} square>
              <Icon
                name={deviceTypeIcon(d.device_type)}
                size={15}
                color={d.online ? p.signal : p.mutedForeground}
              />
            </IconBubble>
          }
          detail={d.machine_number || undefined}>
          {d.device_name}
        </NameCell>
      ),
    },
    {
      key: 'type',
      sortKey: 'type',
      title: t('home.devices.colType'),
      // The kind is the first thing to go when the window narrows: the glyph in the
      // name cell already carries it, and the word is the confirmation.
      width: 116,
      minWidth: 1280,
      render: d => <Cell tone={p.mutedForeground}>{t(deviceTypeLabelKey(d.device_type))}</Cell>,
    },
    {
      key: 'location',
      sortKey: 'location',
      title: t('home.devices.colLocation'),
      flex: 1.4,
      minWidth: 1180,
      render: d => <Cell tone={p.mutedForeground}>{d.location_label || '—'}</Cell>,
    },
    {
      key: 'department',
      sortKey: 'department',
      title: t('reports.departmentFilter'),
      flex: 1.1,
      minWidth: 1440,
      render: d => <Cell tone={p.mutedForeground}>{d.department_name || '—'}</Cell>,
    },
    {
      key: 'personnel',
      sortKey: 'personnel',
      title: t('home.devices.colPersonnel'),
      width: 78,
      minWidth: 1360,
      align: 'right',
      render: d => <Cell mono>{String(d.personnel_count)}</Cell>,
    },
    {
      key: 'status',
      sortKey: 'status',
      title: t('home.devices.colStatus'),
      width: 104,
      render: d => (
        <StatusPill
          tone={deviceTone(d.online)}
          size="sm"
          label={t(d.online ? 'home.devices.online' : 'home.devices.offline')}
        />
      ),
    },
    {
      key: 'seen',
      sortKey: 'seen',
      title: t('home.devices.colLastSeen'),
      width: 106,
      align: 'right',
      render: d => (
        <Cell mono tone={p.mutedForeground}>
          {formatTimestamp(d.last_seen_at)}
        </Cell>
      ),
    },
  ];

  /*
    Тот же список, что на «Явке».

    Раньше устройства жили в пристыкованной панели с собственным тулбаром, а на
    телефоне — лентой с собственной шапкой, и эти два набора расходились: поиск и
    фильтры на мониторе были, на телефоне сначала не применялись вовсе. Теперь
    устройство одно (`shared/ui/data-list`), и расходиться нечему.
  */
  const list = (
    <DataList<DeviceSummary>
      testID="device"
      rows={rows}
      total={items.length}
      columns={cols}
      keyOf={d => d.device_id}
      accessors={ACCESSORS}
      initialSort={NAME_SORT}
      sortControl={{value: sortState, onChange: setSortState}}
      pageControl={{value: pageParam, onChange: setPageParam}}
      perPageControl={{value: pageSize, onChange: setPageSize}}
      perPageOptions={PAGE_SIZES}
      onRowPress={d => setSelectedId(d.device_id)}
      selectedKey={selectedId}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: t('home.devices.searchPlaceholder'),
      }}
      filters={filters}
      dirty={dirty}
      onReset={() => {
        setStatus('all');
        setKind('');
        setDepartmentId('');
        setIncludeDesktops(false);
      }}
      loading={query.isPending}
      skeleton={<SkeletonList count={5} avatar={false} />}
      empty={empty}
      noMatches={empty}
      cardColumns={columns}
      phoneSorts={PHONE_SORTS}
      sortLabel={key => t(PHONE_SORT_LABELS[key] ?? key)}
      renderCard={renderCard}
      footer={
        <>
          <View style={styles.footStat}>
            <StatusDot tone="online" size={8} />
            <AppText variant="caption" color={p.signalInk}>
              {onlineCount} {t('home.devices.online').toLowerCase()}
            </AppText>
          </View>
          <View style={styles.footStat}>
            <StatusDot tone="neutral" size={8} />
            <AppText variant="caption">
              {rows.length - onlineCount} {t('home.devices.offline').toLowerCase()}
            </AppText>
          </View>
        </>
      }
    />
  );

  if (isDesktop) {
    const selected = items.find(d => d.device_id === selectedId) ?? null;
    return (
      <View style={[styles.deskFrame, insets]}>
        <SplitView
          list={list}
          detail={
            selected && renderDetail
              ? renderDetail(selected.device_id, selected.device_name)
              : null
          }
          detailKey={selected?.device_id ?? null}
          detailTitle={selected?.device_name}
          onCloseDetail={() => setSelectedId(null)}
        />
      </View>
    );
  }

  return <View style={[styles.list, insets]}>{list}</View>;
}
